import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-page fade-in">
      <div className="privacy-container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="privacy-subtitle">How we collect, use, and protect your data</p>
        </div>

        <div className="privacy-content">
          <div className="privacy-last-updated">
            <p>Last updated: March 1, 2026</p>
          </div>

          <div className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to BeanBuzz ("we," "our," or "us"). We are committed to protecting your
              personal information and your right to privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you use our cafe management
              platform and related services.
            </p>
            <p>
              By using BeanBuzz, you agree to the collection and use of information in accordance
              with this policy. If you do not agree with the terms of this Privacy Policy, please
              do not access or use our services.
            </p>
          </div>

          <div className="privacy-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Account Information</h3>
            <p>
              When you create an account with BeanBuzz, we collect the following information:
            </p>
            <ul className="privacy-list">
              <li>Email address</li>
              <li>Password (stored in encrypted form)</li>
              <li>Display name (if provided)</li>
              <li>Authentication data from third-party providers (e.g., Google Sign-In)</li>
            </ul>

            <h3>2.2 Cafe and Business Data</h3>
            <p>
              As a cafe management platform, we collect and store data related to your business operations:
            </p>
            <ul className="privacy-list">
              <li>Cafe/organization name and settings</li>
              <li>Menu items, descriptions, and pricing</li>
              <li>Table configurations and QR code data</li>
              <li>Order history and transaction records</li>
              <li>Customer order preferences</li>
            </ul>

            <h3>2.3 Payment Information</h3>
            <p>
              BeanBuzz does not directly process or store payment card details. Any payment
              processing is handled through secure third-party payment processors. We may store
              transaction references and order totals for record-keeping purposes.
            </p>

            <h3>2.4 Usage Data</h3>
            <p>
              We automatically collect certain information when you use our platform:
            </p>
            <ul className="privacy-list">
              <li>Browser type and version</li>
              <li>Pages visited and features used</li>
              <li>Time and date of access</li>
              <li>Device information</li>
              <li>IP address</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="privacy-list">
              <li>To provide and maintain our cafe management services</li>
              <li>To authenticate your identity and manage your account</li>
              <li>To process and manage orders placed through our platform</li>
              <li>To improve and optimize our platform's performance and user experience</li>
              <li>To communicate with you about your account, updates, and support</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>4. Cookies and Tracking Technologies</h2>
            <p>
              BeanBuzz uses cookies and similar tracking technologies to enhance your experience
              on our platform. These include:
            </p>
            <ul className="privacy-list">
              <li><strong>Essential cookies:</strong> Required for the platform to function properly, including authentication and session management</li>
              <li><strong>Preference cookies:</strong> Used to remember your settings and preferences, such as language selection</li>
              <li><strong>Analytics cookies:</strong> Help us understand how users interact with our platform so we can improve our services</li>
            </ul>
            <p>
              You can manage your cookie preferences through your browser settings. Please note
              that disabling essential cookies may affect the functionality of our platform.
            </p>
          </div>

          <div className="privacy-section">
            <h2>5. Third-Party Services</h2>
            <p>
              BeanBuzz integrates with the following third-party services that may collect and
              process your data:
            </p>

            <h3>5.1 Firebase (Google)</h3>
            <p>
              We use Firebase for authentication, hosting, and data storage. Firebase may collect
              usage data, device information, and authentication tokens. For more information,
              please refer to{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="privacy-link">
                Firebase's Privacy Policy
              </a>.
            </p>

            <h3>5.2 Google Authentication</h3>
            <p>
              If you choose to sign in with Google, we receive your basic profile information
              (name and email address) from Google. We do not access your Google contacts,
              calendar, or other personal data.
            </p>
          </div>

          <div className="privacy-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as
              needed to provide you our services. Specifically:
            </p>
            <ul className="privacy-list">
              <li><strong>Account data:</strong> Retained until you delete your account or request data removal</li>
              <li><strong>Order and transaction data:</strong> Retained for the duration of your account plus any legally required retention period</li>
              <li><strong>Usage and analytics data:</strong> Retained in anonymized form for up to 24 months</li>
              <li><strong>Cafe and menu data:</strong> Retained until you delete your organization or request removal</li>
            </ul>
            <p>
              When data is no longer needed, we securely delete or anonymize it in accordance
              with our data retention procedures.
            </p>
          </div>

          <div className="privacy-section">
            <h2>7. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect
              your personal information, including:
            </p>
            <ul className="privacy-list">
              <li>Encryption of data in transit using TLS/SSL</li>
              <li>Secure password hashing and storage</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
            </ul>
            <p>
              While we strive to use commercially acceptable means to protect your personal
              information, no method of transmission over the Internet or method of electronic
              storage is 100% secure.
            </p>
          </div>

          <div className="privacy-section">
            <h2>8. Your Rights (GDPR)</h2>
            <p>
              If you are located in the European Economic Area (EEA), you have certain data
              protection rights under the General Data Protection Regulation (GDPR). These include:
            </p>
            <ul className="privacy-list">
              <li><strong>Right of access:</strong> You can request a copy of the personal data we hold about you</li>
              <li><strong>Right to rectification:</strong> You can request that we correct any inaccurate or incomplete data</li>
              <li><strong>Right to erasure:</strong> You can request that we delete your personal data under certain conditions</li>
              <li><strong>Right to restrict processing:</strong> You can request that we limit how we use your data</li>
              <li><strong>Right to data portability:</strong> You can request a copy of your data in a structured, machine-readable format</li>
              <li><strong>Right to object:</strong> You can object to our processing of your personal data</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the contact information
              provided below. We will respond to your request within 30 days.
            </p>
          </div>

          <div className="privacy-section">
            <h2>9. Children's Privacy</h2>
            <p>
              BeanBuzz is not intended for use by individuals under the age of 16. We do not
              knowingly collect personal information from children. If we become aware that we
              have collected personal data from a child under 16, we will take steps to delete
              that information promptly.
            </p>
          </div>

          <div className="privacy-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date. You are advised to review this Privacy Policy periodically for any
              changes.
            </p>
          </div>

          <div className="privacy-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, your personal data, or would
              like to exercise your data protection rights, please contact us:
            </p>
            <div className="privacy-contact-card">
              <p><strong>Email:</strong>{' '}
                <a href="mailto:vadym@suprun.ca" className="privacy-link">vadym@suprun.ca</a>
              </p>
              <p><strong>Subject:</strong> Privacy Policy Inquiry</p>
            </div>
          </div>
        </div>

        <div className="privacy-footer">
          <Link to="/" className="back-link">&larr; Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
